import {User} from '../../../models/mfl';
import {UserStore} from '../../../stores/userStore';
import {AccountPasswordChangeFormData} from '../schemas/authSchemas';

/**
 * Builds the payload for password change by combining form values with userStore data.
 *
 * The backend requires all user fields when updating, even when only changing password.
 * This helper merges the password fields from the form with the existing user data
 * from userStore.
 *
 * @param formValues - Password change form data (password + confirmPassword + bypass)
 * @param userStore - User store containing existing user data
 * @returns Complete User object for updateUserInfo() call
 */
export function buildPasswordChangePayload(
  formValues: AccountPasswordChangeFormData,
  userStore: UserStore,
): User {
  return {
    // Password fields from form (what user entered)
    password: formValues.password,
    passwordValidationBypass: formValues.passwordValidationBypass,
    // All other required fields from userStore (existing data)
    firstName: userStore.firstName,
    lastName: userStore.lastName,
    email: userStore.emailAddress,
    dateOfBirth: userStore.dateOfBirth,
    phoneNumber: userStore.phoneNumber,
    billingAddress: userStore.billingAddress,
  };
}

/**
 * Prepares minimal user payload for password strength validation API call.
 * Only includes fields required by the /ecommerce/validate-user endpoint.
 *
 * @param password - The password to validate
 * @param userStore - User store containing existing user data (for email)
 * @returns Minimal User object for checkPasswordStrength() call
 */
export function preparePasswordValidationPayload(password: string, userStore: UserStore): User {
  return {
    password,
    email: userStore.emailAddress,
    firstName: userStore.firstName,
    lastName: userStore.lastName,
    phoneNumber: userStore.phoneNumber,
    dateOfBirth: userStore.dateOfBirth,
    billingAddress: userStore.billingAddress,
    passwordValidationBypass: false, // Always false for validation check
  };
}
